import { useApolloClient } from '@apollo/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useListMasterclassUserStatisticsQuery,
  useUpdateMasterclassUserStatisticsMutation,
} from '@/@generated/codegen/API';

export const CASE_STATISTICS_QUERY_KEYS = {
  all: ['case_statistics'] as const,
  byCase: (caseId: string, masterclassId: string) =>
    ['case_statistics', 'case', caseId, 'masterclass', masterclassId] as const,
  byUserAndCase: (userId: string, caseId: string, masterclassId: string) =>
    ['case_statistics', 'user', userId, 'case', caseId, 'masterclass', masterclassId] as const,
};

export const useCaseStatisticsByCase = (caseId: string | null, masterclassId: string | null) => {
  const { data, loading, error, refetch } = useListMasterclassUserStatisticsQuery({
    variables: {
      where: {
        caseId: { equals: caseId || '' },
        masterclassId: { equals: masterclassId || '' },
      },
    },
    skip: !caseId || !masterclassId,
  });

  // Note: User relation not available in fragment, only email can be retrieved
  const statisticsData = data?.listMasterclassUserStatistics?.edges.map((edge) => ({
    id: edge.node.id,
    userId: edge.node.userId,
    caseId: edge.node.caseId,
    caseSelectorVisits: edge.node.caseSelectorVisits,
    caseClicks: edge.node.caseClicks,
    completions: edge.node.completions,
    createdAt: edge.node.createdAt,
    updatedAt: edge.node.updatedAt,
    // profiles field not available - User entity doesn't have username
    profiles: null,
  }));

  return {
    data: statisticsData,
    isLoading: loading,
    error: error,
    refetch,
  };
};

export const useCaseStatisticsByUserAndCase = (
  userId: string | null,
  caseId: string | null,
  masterclassId: string | null
) => {
  const { data, loading, error, refetch } = useListMasterclassUserStatisticsQuery({
    variables: {
      where: {
        userId: { equals: userId || '' },
        caseId: { equals: caseId || '' },
        masterclassId: { equals: masterclassId || '' },
      },
      paginationArgs: { first: 1 },
    },
    skip: !userId || !caseId || !masterclassId,
  });

  const statisticsData = data?.listMasterclassUserStatistics?.edges[0]
    ? {
        id: data.listMasterclassUserStatistics.edges[0].node.id,
        userId: data.listMasterclassUserStatistics.edges[0].node.userId,
        caseId: data.listMasterclassUserStatistics.edges[0].node.caseId,
        caseSelectorVisits: data.listMasterclassUserStatistics.edges[0].node.caseSelectorVisits,
        caseClicks: data.listMasterclassUserStatistics.edges[0].node.caseClicks,
        completions: data.listMasterclassUserStatistics.edges[0].node.completions,
        createdAt: data.listMasterclassUserStatistics.edges[0].node.createdAt,
        updatedAt: data.listMasterclassUserStatistics.edges[0].node.updatedAt,
      }
    : null;

  return {
    data: statisticsData,
    isLoading: loading,
    error: error,
    refetch,
  };
};

export const useUpsertCaseStatistics = () => {
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const [updateMutation] = useUpdateMasterclassUserStatisticsMutation();

  return useMutation({
    mutationFn: async ({
      userId,
      caseId,
      masterclassId,
      updates,
    }: {
      userId: string;
      caseId: string;
      masterclassId: string;
      updates: {
        case_selector_visits?: number;
        case_clicks?: number;
        completions?: number;
      };
    }) => {
      const result = await updateMutation({
        variables: {
          data: {
            caseId,
            masterclassId,
            // Backend uses boolean flags to increment counts
            increaseCaseSelectorVisit: updates.case_selector_visits !== undefined,
            increaseCaseClicks: updates.case_clicks !== undefined,
            increaseCompletions: updates.completions !== undefined,
          },
        },
      });

      if (result.errors) throw new Error(result.errors[0].message);
      if (!result.data?.updateMasterclassUserStatistics)
        throw new Error('Failed to update statistics');

      return {
        id: result.data.updateMasterclassUserStatistics.id,
        userId: result.data.updateMasterclassUserStatistics.userId,
        caseId: result.data.updateMasterclassUserStatistics.caseId,
        masterclassId,
        caseSelectorVisits: result.data.updateMasterclassUserStatistics.caseSelectorVisits,
        caseClicks: result.data.updateMasterclassUserStatistics.caseClicks,
        completions: result.data.updateMasterclassUserStatistics.completions,
        createdAt: result.data.updateMasterclassUserStatistics.createdAt,
        updatedAt: result.data.updateMasterclassUserStatistics.updatedAt,
      };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: CASE_STATISTICS_QUERY_KEYS.byUserAndCase(
          data.userId,
          data.caseId,
          data.masterclassId
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: CASE_STATISTICS_QUERY_KEYS.byCase(data.caseId, data.masterclassId),
      });
      void queryClient.invalidateQueries({ queryKey: CASE_STATISTICS_QUERY_KEYS.all });
      void apolloClient.refetchQueries({
        include: ['listMasterclassUserStatistics'],
      });
    },
  });
};
